import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "../../utils/classMerger";
import { bandUnderPrompter, noseTarget, PROMPTER_BOTTOM } from "../../utils/captionGuide";
import type { CaptionPosition } from "../../types/captionPosition";
import type { CaptionStyle } from "../../types/captionStyle";
import type { FaceAlignmentState } from "../../hooks/useFaceAlignment";

// Where the captions will land and where to put your nose, over the recorder's preview.
// DOM only, like the teleprompter: the recorder captures camera frames, so none of this is in the video.
// Portrait only: the captioned recorder never records landscape.
interface CaptionGuideOverlayProps {
    position: CaptionPosition;
    captionStyle: CaptionStyle;
    prompterOnTop: boolean;
    recording: boolean;
    // Face tracking, when it runs: the ring and the label react. Absent or off: the plain target.
    alignment?: FaceAlignmentState;
}

const pillClass = "whitespace-nowrap rounded-full px-2 py-[3px] text-xs font-semibold tracking-[.02em] text-gray-900";
// The label beside the frame plus its line to the box.
const OUTSIDE_LABEL_PX = 190;
// Where the label sits when it has to go under the script instead.
const UNDER_LABEL_TOP = PROMPTER_BOTTOM + 1.6;

// Room to the right of the video box, inside the camera area: the box is this overlay's parent.
const useSideRoom = (ref: React.RefObject<HTMLDivElement>): boolean => {
    const [room, setRoom] = useState(false);
    useLayoutEffect(() => {
        const frame = ref.current?.parentElement;
        const area = frame?.parentElement;
        if (!frame || !area) return;
        const measure = (): void => {
            setRoom(area.getBoundingClientRect().right - frame.getBoundingClientRect().right >= OUTSIDE_LABEL_PX);
        };
        measure();
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);
            return () => window.removeEventListener("resize", measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(area);
        return () => observer.disconnect();
    }, [ref]);
    return room;
};

const cornerClass = "absolute h-[19%] w-[20%] border-white/85";

const CaptionGuideOverlay: React.FC<CaptionGuideOverlayProps> = ({ position, captionStyle, prompterOnTop, recording, alignment }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const sideRoom = useSideRoom(rootRef);
    const target = noseTarget(position, prompterOnTop, captionStyle);
    const { band } = target;
    // Under the script the box stays, but its fill and label would hide the words: the label moves out.
    const labelOutside = bandUnderPrompter(band, prompterOnTop);
    const bandCenter = band.top + band.height / 2;
    const bandBottom = band.top + band.height;
    // A box that reaches a frame edge is open on that side: the captions run to the edge.
    const flushTop = band.top <= 0;
    const flushBottom = bandBottom >= 100;
    const hinting = alignment?.status === "hint";
    const linedUp = alignment?.status === "good";
    const noseLabel = hinting ? alignment.hint : linedUp ? "Lined up" : "Center your nose here";

    return (
        // z-20 over the teleprompter's z-10: the band is drawn through the script, and taps pass to it.
        <div
            ref={rootRef}
            data-testid="caption-guide"
            aria-hidden="true"
            className={cn("pointer-events-none absolute inset-0 z-20 transition-opacity duration-300", recording && "opacity-30")}
        >
            <div className="absolute inset-0 overflow-hidden">
                <div
                    data-testid="caption-band"
                    className={cn(
                        "absolute left-[4%] right-[4%] flex items-center justify-center rounded-md border-[1.5px] border-dashed border-white/85",
                        flushTop && "border-t-0 rounded-t-none",
                        flushBottom && "border-b-0 rounded-b-none",
                        !labelOutside && "bg-secondary/40"
                    )}
                    style={{ top: `${band.top}%`, height: `${band.height}%` }}
                >
                    {!recording && !labelOutside && <span className={cn(pillClass, "bg-secondary/95")}>Captions appear here</span>}
                </div>
                <span data-testid="center-line" className="absolute left-1/2 top-0 bottom-0 w-0 border-l border-dashed border-white/50" />
                <div
                    data-testid="face-zone"
                    className="absolute left-[33%] right-[33%]"
                    style={{ top: `${target.zoneTop}%`, height: `${target.zoneBottom - target.zoneTop}%` }}
                >
                    <span data-corner="tl" className={cn(cornerClass, "left-0 top-0 border-l-[1.5px] border-t-[1.5px]")} />
                    <span data-corner="tr" className={cn(cornerClass, "right-0 top-0 border-r-[1.5px] border-t-[1.5px]")} />
                    <span data-corner="bl" className={cn(cornerClass, "bottom-0 left-0 border-b-[1.5px] border-l-[1.5px]")} />
                    <span data-corner="br" className={cn(cornerClass, "bottom-0 right-0 border-b-[1.5px] border-r-[1.5px]")} />
                </div>
                <span
                    data-testid="nose-ring"
                    className={cn(
                        "absolute left-1/2 aspect-square w-[11%] -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px]",
                        linedUp ? "border-solid border-[#67cc8e]" : hinting ? "border-dashed border-[#ffbe00]" : "border-dashed border-white/90"
                    )}
                    style={{ top: `${target.nose}%` }}
                >
                    <span
                        className={cn(
                            "absolute left-1/2 top-1/2 h-[14%] w-[14%] -translate-x-1/2 -translate-y-1/2 rounded-full",
                            linedUp ? "bg-[#67cc8e]" : "bg-white"
                        )}
                    />
                </span>
                {!recording && target.label && (
                    <span
                        className={cn(
                            pillClass,
                            "absolute left-1/2 -translate-x-1/2",
                            linedUp ? "bg-[#67cc8e]" : hinting ? "bg-[#ffbe00]" : "bg-white/90"
                        )}
                        style={{ top: `${target.label.top}%` }}
                    >
                        {noseLabel}
                    </span>
                )}
                {!recording && labelOutside && !sideRoom && (
                    // An arrow from the label up to the box's bottom edge, through the script's control row.
                    <>
                        <span
                            data-testid="caption-band-pointer"
                            className="absolute left-1/2 w-0 -translate-x-1/2 border-l-[1.5px] border-dashed border-white/85"
                            style={{ top: `${bandBottom}%`, height: `${UNDER_LABEL_TOP - bandBottom}%` }}
                        />
                        <ArrowUp className="absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-full text-white/90" style={{ top: `${bandBottom}%` }} />
                        <span className={cn(pillClass, "absolute left-1/2 -translate-x-1/2 bg-secondary/95")} style={{ top: `${UNDER_LABEL_TOP}%` }}>
                            Captions appear here
                        </span>
                    </>
                )}
            </div>
            {!recording && labelOutside && sideRoom && (
                <>
                    <span
                        data-testid="caption-band-pointer"
                        className="absolute h-0 border-t-[1.5px] border-dashed border-white/85"
                        style={{ top: `${bandCenter}%`, left: "96%", width: "calc(4% + 14px)" }}
                    />
                    <span
                        className={cn(pillClass, "absolute -translate-y-1/2 bg-secondary/95")}
                        style={{ top: `${bandCenter}%`, left: "calc(100% + 14px)" }}
                    >
                        Captions appear here
                    </span>
                </>
            )}
        </div>
    );
};

export default CaptionGuideOverlay;
